/**
 * 플랫폼 사용자 상세 화면.
 * 레거시 `ruda-admin-web/pages/admin/userDetailInfo.js` 섹션을 상단 탭으로 유지:
 * 기본 정보 | 사업자 정보 | 판촉영업 위탁 계약서
 */
import {
  assertUserDocumentFile,
  BUSINESS_OWNER_TYPE,
  isInvalidUploadKeyError,
  REUPLOAD_FILE_MESSAGE,
  requireUploadKey,
  USER_DOCUMENT_ACCEPT,
  type BusinessOwnerType,
  type PlatformUserDetailResponse,
  type PlatformUserDetailUpdateRequest,
  type TermsAgreementItem,
} from '@/entities/user/api/user.api';
import {
  useBankbookPresignedUpload,
  useBusinessLicensePresignedUpload,
  useMaxMatchLiteCurrentMonthUsage,
  usePlatformUserDetail,
  usePlatformUserLoginMethods,
  usePromoPresignedUpload,
  useResetBusinessLicense,
  useResetMaxMatchLiteUsage,
  useResetPromoLicense,
  useUpdatePlatformUser,
  useWithdrawPlatformUser,
} from '@/entities/user/hooks/usePlatformUsers';
import {
  useOcrBusinessLicense,
  useOcrPromoLicense,
} from '@/entities/ocr/hooks/useOcr';
import {
  UserConsignmentContractTab,
  type ConsignmentTabHandle,
} from '@/pages/users/UserConsignmentContractTab';
import { UserLoginMethodsCard } from '@/pages/users/UserLoginMethodsCard';
import {
  buildBusinessOwnerSaveFields,
  getSalesApprovalEligibility,
  isIndividualBusinessOwner,
  isPastIsoDate,
  mapBusinessLicenseOcrToForm,
  mapPromoLicenseOcrToForm,
  resolveTermsRows,
  resolveTrainingStatusForSave,
} from '@/pages/users/userDetailUtils';
import {
  SALES_APPROVAL_OPTIONS,
  VERIFICATION_STATUS_OPTIONS,
  verificationStatusLabel,
} from '@/pages/users/userStatusUtils';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { AddressSearchField } from '@/shared/components/AddressSearchField';
import { useConfirmDialog } from '@/shared/components/ConfirmDialogProvider';
import { DatePicker } from '@/shared/components/DatePicker';
import { DownloadWithPreview } from '@/shared/components/DownloadWithPreview';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  formatDate,
  formatDateTime,
  formatDateTimeMinute,
} from '@/shared/lib/dateFormat';
import { ToolbarRow } from '@/shared/components/ui/row';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import {
  getPresignedUploadContentType,
  uploadFileToPresignedUrl,
} from '@/shared/utils/fileUpload';
import { formatBusinessRegistrationNumber } from '@/shared/utils/businessRegistrationNumber';
import { handleApiError, useToast } from '@/shared/utils/toast';
import {
  useBlocker,
  useNavigate,
  useParams,
  useRouter,
} from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { ScanLine } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type VerificationStatus = PlatformUserDetailResponse['promoLicenseStatus'];
type BusinessVerificationStatus = NonNullable<
  PlatformUserDetailUpdateRequest['businessVerificationStatus']
>;
type FinancialStatus = NonNullable<
  PlatformUserDetailUpdateRequest['financialVerificationStatus']
>;
type SalesApprovalStatus = NonNullable<
  PlatformUserDetailUpdateRequest['salesApprovalStatus']
>;

type DetailTab = 'basic' | 'business' | 'consignment';

const DETAIL_TABS: { key: DetailTab; label: string }[] = [
  { key: 'basic', label: '기본 정보' },
  { key: 'business', label: '사업자 정보' },
  { key: 'consignment', label: '판촉영업 위탁 계약서' },
];

const BUSINESS_VERIFICATION_OPTIONS = [
  ...VERIFICATION_STATUS_OPTIONS,
  'BUSINESS_TYPE_NOT_ALLOWED',
] as const;

/** 레거시: 미등록·확인 전은 선택 불가(표시만) */
const LOCKED_VERIFICATION_OPTIONS = ['NOT_REGISTERED', 'PENDING'] as const;

/** 레거시 계좌 인증 Select 옵션 */
const FINANCIAL_EDIT_OPTIONS = ['NOT_REGISTERED', 'VERIFIED'] as const;

/** 대표자 생년월일 — 기본 DatePicker(최근 30년)로는 1980년대 등을 고를 수 없다 */
const BIRTH_DATE_START_MONTH = new Date(1920, 0);

const isTodayOrFuture = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date >= today;
};

/** 저장 대상 프로파일 스냅샷 — dirty 비교용 */
type ProfileSnapshot = {
  phoneNumber: string;
  isActive: boolean;
  remarks: string;
  companyName: string;
  ceoName: string;
  address: string;
  taxInvoiceEmail: string;
  businessLicenseNumber: string;
  businessOwnerType: string;
  birthDate: string;
  businessType: string;
  businessClass: string;
  bankName: string;
  bankAccountNumber: string;
  accountHolder: string;
  promoLicenseNumber: string;
  promoLicenseValidFrom: string;
  promoLicenseValidTo: string;
  businessVerificationStatus: string;
  financialVerificationStatus: string;
  promoLicenseStatus: string;
  salesApprovalStatus: string;
  pendingBusinessLicenseKey: string | null;
  pendingBankbookKey: string | null;
  pendingPromoKey: string | null;
};

const normalizeProfileSnapshot = (s: ProfileSnapshot): ProfileSnapshot => ({
  phoneNumber: s.phoneNumber.trim(),
  isActive: Boolean(s.isActive),
  remarks: s.remarks.trim(),
  companyName: s.companyName.trim(),
  ceoName: s.ceoName.trim(),
  address: s.address.trim(),
  taxInvoiceEmail: s.taxInvoiceEmail.trim(),
  businessLicenseNumber: s.businessLicenseNumber.trim(),
  businessOwnerType: s.businessOwnerType,
  birthDate: s.birthDate.trim(),
  businessType: s.businessType.trim(),
  businessClass: s.businessClass.trim(),
  bankName: s.bankName.trim(),
  bankAccountNumber: s.bankAccountNumber.trim(),
  accountHolder: s.accountHolder.trim(),
  promoLicenseNumber: s.promoLicenseNumber.trim(),
  promoLicenseValidFrom: s.promoLicenseValidFrom.trim(),
  promoLicenseValidTo: s.promoLicenseValidTo.trim(),
  businessVerificationStatus: s.businessVerificationStatus,
  financialVerificationStatus: s.financialVerificationStatus,
  promoLicenseStatus: s.promoLicenseStatus,
  salesApprovalStatus: s.salesApprovalStatus,
  pendingBusinessLicenseKey: s.pendingBusinessLicenseKey,
  pendingBankbookKey: s.pendingBankbookKey,
  pendingPromoKey: s.pendingPromoKey,
});

const isSameProfileSnapshot = (a: ProfileSnapshot, b: ProfileSnapshot) =>
  JSON.stringify(normalizeProfileSnapshot(a)) ===
  JSON.stringify(normalizeProfileSnapshot(b));

const parseTermsAgreements = (
  raw: PlatformUserDetailResponse['termsAgreements'],
): TermsAgreementItem[] => {
  if (!raw || typeof raw !== 'object') return [];
  const agreements = (raw as { agreements?: TermsAgreementItem[] }).agreements;
  return Array.isArray(agreements) ? agreements : [];
};

const FileUploadField: React.FC<{
  label: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  uploading: boolean;
  pendingKey: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ label, inputRef, uploading, pendingKey, onChange }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <FileUploadButton
      inputRef={inputRef}
      accept={USER_DOCUMENT_ACCEPT}
      disabled={uploading}
      resetOnSelect={false}
      onChange={onChange}
    >
      파일 업로드
    </FileUploadButton>
    {pendingKey ? (
      <p className="text-sm text-ruda-success">
        업로드 완료. 저장 시 서버에 반영됩니다.
      </p>
    ) : null}
  </div>
);

export const UserDetailPage: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const navigate = useNavigate();
  const router = useRouter();
  const { canWrite, readOnly } = useMenuAccess();
  const promoInputRef = useRef<HTMLInputElement>(null);
  const businessLicenseInputRef = useRef<HTMLInputElement>(null);
  const bankbookInputRef = useRef<HTMLInputElement>(null);
  const consignmentRef = useRef<ConsignmentTabHandle>(null);
  // 나가기 확인 시 다이얼로그 close가 reset을 호출해도 proceed를 덮어쓰지 않게 한다
  const leaveConfirmedRef = useRef(false);
  const [profileBaseline, setProfileBaseline] =
    useState<ProfileSnapshot | null>(null);
  const [profileHydrated, setProfileHydrated] = useState(false);
  const { userId: userIdParam } = useParams({ strict: false }) as {
    userId?: string;
  };
  // 경로는 사용자 ID(양의 정수)만 받는다. 예전 이메일 경로 북마크 등은 잘못된 경로로 안내한다.
  const userId =
    userIdParam && /^\d+$/.test(userIdParam) ? Number(userIdParam) : null;

  const { data, isLoading, isError, error, refetch } =
    usePlatformUserDetail(userId);
  const updateUser = useUpdatePlatformUser();
  const promoUpload = usePromoPresignedUpload();
  const businessLicenseUpload = useBusinessLicensePresignedUpload();
  const bankbookUpload = useBankbookPresignedUpload();
  const ocrBusinessLicense = useOcrBusinessLicense();
  const ocrPromoLicense = useOcrPromoLicense();
  const resetPromoLicense = useResetPromoLicense();
  const resetBusinessLicense = useResetBusinessLicense();
  const withdrawUser = useWithdrawPlatformUser();
  const resetMaxMatchLite = useResetMaxMatchLiteUsage();

  const {
    data: maxMatchLiteUsage,
    isLoading: maxMatchLiteLoading,
    refetch: refetchMaxMatchLite,
  } = useMaxMatchLiteCurrentMonthUsage(data?.userId ?? null);

  const [pendingBusinessLicenseKey, setPendingBusinessLicenseKey] = useState<
    string | null
  >(null);
  const [pendingBankbookKey, setPendingBankbookKey] = useState<string | null>(
    null,
  );
  const [pendingPromoKey, setPendingPromoKey] = useState<string | null>(null);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [remarks, setRemarks] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [ceoName, setCeoName] = useState('');
  const [address, setAddress] = useState('');
  const [taxInvoiceEmail, setTaxInvoiceEmail] = useState('');
  const [businessLicenseNumber, setBusinessLicenseNumber] = useState('');
  const [businessOwnerType, setBusinessOwnerType] = useState<
    BusinessOwnerType | ''
  >('');
  const [birthDate, setBirthDate] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [businessClass, setBusinessClass] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [promoLicenseNumber, setPromoLicenseNumber] = useState('');
  const [promoLicenseValidFrom, setPromoLicenseValidFrom] = useState('');
  const [promoLicenseValidTo, setPromoLicenseValidTo] = useState('');
  const [businessVerificationStatus, setBusinessVerificationStatus] =
    useState<BusinessVerificationStatus>('NOT_REGISTERED');
  const [financialVerificationStatus, setFinancialVerificationStatus] =
    useState<FinancialStatus>('NOT_REGISTERED');
  const [promoLicenseStatus, setPromoLicenseStatus] =
    useState<VerificationStatus>('NOT_REGISTERED');
  const [salesApprovalStatus, setSalesApprovalStatus] =
    useState<SalesApprovalStatus>('TRANSACTION_IMPOSSIBLE');
  // dirty 비교용 — Select에는 계산값을 보여도, 서버 값과 다르면 저장을 켠다
  const [serverSalesApprovalStatus, setServerSalesApprovalStatus] =
    useState<SalesApprovalStatus | null>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>('basic');
  const [maxMatchResetOpen, setMaxMatchResetOpen] = useState(false);
  const [maxMatchResetReason, setMaxMatchResetReason] = useState('');
  const [consignmentDirty, setConsignmentDirty] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [quitDialogOpen, setQuitDialogOpen] = useState(false);

  /** 서버 상세 응답을 편집 폼에 반영 (최초 로드·저장 후 refetch 결과) */
  const applyDetailToForm = useCallback((user: PlatformUserDetailResponse) => {
    setPhoneNumber(user.phoneNumber ?? '');
    setIsActive(Boolean(user.isActive));
    setRemarks(user.remarks ?? '');
    setCompanyName(user.companyName ?? '');
    setCeoName(user.ceoName ?? '');
    setAddress(user.address ?? '');
    setTaxInvoiceEmail(user.taxInvoiceEmail ?? '');
    setBusinessLicenseNumber(
      formatBusinessRegistrationNumber(user.businessLicenseNumber ?? ''),
    );
    setBusinessOwnerType(
      user.businessOwnerType === BUSINESS_OWNER_TYPE.INDIVIDUAL ||
        user.businessOwnerType === BUSINESS_OWNER_TYPE.CORPORATE
        ? user.businessOwnerType
        : '',
    );
    setBirthDate(user.birthDate?.slice(0, 10) ?? '');
    setBusinessType(user.businessType ?? '');
    setBusinessClass(user.businessClass ?? '');
    setBankName(user.bankName ?? '');
    setBankAccountNumber(user.bankAccountNumber ?? '');
    setAccountHolder(user.accountHolder ?? '');
    setPromoLicenseNumber(user.promoLicenseNumber ?? '');
    setPromoLicenseValidFrom(user.promoLicenseValidFrom?.slice(0, 10) ?? '');
    setPromoLicenseValidTo(user.promoLicenseValidTo?.slice(0, 10) ?? '');
    setBusinessVerificationStatus(
      (user.businessVerificationStatus as BusinessVerificationStatus) ||
        'NOT_REGISTERED',
    );
    setFinancialVerificationStatus(
      (user.financialVerificationStatus as FinancialStatus) || 'NOT_REGISTERED',
    );
    setPromoLicenseStatus(user.promoLicenseStatus);
    const salesStatus =
      (user.salesApprovalStatus as SalesApprovalStatus) ||
      'TRANSACTION_IMPOSSIBLE';
    setServerSalesApprovalStatus(salesStatus);
    setSalesApprovalStatus(salesStatus);
    setPendingBusinessLicenseKey(null);
    setPendingBankbookKey(null);
    setPendingPromoKey(null);
    setProfileBaseline(null);
  }, []);

  useEffect(() => {
    if (!data || profileHydrated) return;
    // 탭 전환·백그라운드 리페치로 편집 중 값이 덮이지 않도록 최초 로드에만 effect로 반영
    applyDetailToForm(data);
    setProfileHydrated(true);
  }, [data, profileHydrated, applyDetailToForm]);

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  const salesEligibility = useMemo(
    () =>
      getSalesApprovalEligibility(
        businessVerificationStatus,
        financialVerificationStatus,
        promoLicenseStatus,
      ),
    [
      businessVerificationStatus,
      financialVerificationStatus,
      promoLicenseStatus,
    ],
  );

  const applySalesApprovalFromVerification = (
    business: BusinessVerificationStatus,
    financial: FinancialStatus,
    promo: VerificationStatus,
  ) => {
    setSalesApprovalStatus(
      getSalesApprovalEligibility(business, financial, promo).derived,
    );
  };

  // 인증 3종이 바뀌면 영업 인증을 다시 계산한다. 로드 직후 서버 값과 달라도 계산값을 보여 준다.
  useEffect(() => {
    if (!profileHydrated) return;
    setSalesApprovalStatus(
      getSalesApprovalEligibility(
        businessVerificationStatus,
        financialVerificationStatus,
        promoLicenseStatus,
      ).derived,
    );
  }, [
    profileHydrated,
    businessVerificationStatus,
    financialVerificationStatus,
    promoLicenseStatus,
  ]);

  const termsAgreements = parseTermsAgreements(data?.termsAgreements);
  const termsRows = resolveTermsRows(
    data?.termsAgreementItems,
    termsAgreements,
  );
  // 상세는 이메일 키, 로그인 수단은 userId 키라 상세를 받은 뒤 이어서 조회한다
  const loginMethods = usePlatformUserLoginMethods(data?.userId ?? null);
  // 레거시: 서버 URL 또는 이번 세션 pending 업로드 키가 있으면 관련 필드 활성화
  const hasPromoLicenseFile = Boolean(
    data?.promoLicenseFile || pendingPromoKey,
  );
  const hasBusinessLicenseFile = Boolean(
    data?.businessLicenseFile || pendingBusinessLicenseKey,
  );

  const maxMatchLiteLimit = Number(maxMatchLiteUsage?.maxLimit ?? 0);
  const maxMatchLiteRemaining = Number(maxMatchLiteUsage?.remaining ?? 0);
  const maxMatchLiteCount = Number(maxMatchLiteUsage?.usageCount ?? 0);
  const hasMaxMatchLiteUsage =
    Number.isFinite(maxMatchLiteLimit) && maxMatchLiteLimit > 0;

  const currentProfileSnapshot = useMemo<ProfileSnapshot>(
    () => ({
      phoneNumber,
      isActive,
      remarks,
      companyName,
      ceoName,
      address,
      taxInvoiceEmail,
      businessLicenseNumber,
      businessOwnerType,
      birthDate,
      businessType,
      businessClass,
      bankName,
      bankAccountNumber,
      accountHolder,
      promoLicenseNumber,
      promoLicenseValidFrom,
      promoLicenseValidTo,
      businessVerificationStatus,
      financialVerificationStatus,
      promoLicenseStatus,
      salesApprovalStatus,
      pendingBusinessLicenseKey,
      pendingBankbookKey,
      pendingPromoKey,
    }),
    [
      phoneNumber,
      isActive,
      remarks,
      companyName,
      ceoName,
      address,
      taxInvoiceEmail,
      businessLicenseNumber,
      businessOwnerType,
      birthDate,
      businessType,
      businessClass,
      bankName,
      bankAccountNumber,
      accountHolder,
      promoLicenseNumber,
      promoLicenseValidFrom,
      promoLicenseValidTo,
      businessVerificationStatus,
      financialVerificationStatus,
      promoLicenseStatus,
      salesApprovalStatus,
      pendingBusinessLicenseKey,
      pendingBankbookKey,
      pendingPromoKey,
    ],
  );

  // 서버 영업 인증을 baseline에 고정한다. 계산값과 다르면 저장이 켜지고, Select를 바꿔도 켜진다.
  useEffect(() => {
    if (!profileHydrated || profileBaseline != null) return;
    if (serverSalesApprovalStatus == null) return;
    setProfileBaseline({
      ...currentProfileSnapshot,
      salesApprovalStatus: serverSalesApprovalStatus,
    });
  }, [
    profileHydrated,
    profileBaseline,
    currentProfileSnapshot,
    serverSalesApprovalStatus,
  ]);

  const isProfileDirty = useMemo(() => {
    if (!profileBaseline) return false;
    return !isSameProfileSnapshot(profileBaseline, currentProfileSnapshot);
  }, [profileBaseline, currentProfileSnapshot]);

  const isFormDirty = isProfileDirty || consignmentDirty;

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty,
    enableBeforeUnload: isFormDirty,
    withResolver: true,
    disabled: !isFormDirty,
  });

  useEffect(() => {
    if (blocker.status === 'blocked') {
      setQuitDialogOpen(true);
    }
  }, [blocker.status]);

  const handleConsignmentDirtyChange = useCallback((dirty: boolean) => {
    setConsignmentDirty(dirty);
  }, []);

  const handleSave = async () => {
    if (!data) return;
    if (promoLicenseStatus === 'APPROVED' && !promoLicenseNumber.trim()) {
      toast.error('판촉 신고증 승인 시 신고번호가 필요합니다.');
      return;
    }
    if (
      isIndividualBusinessOwner(businessOwnerType) &&
      birthDate &&
      !isPastIsoDate(birthDate)
    ) {
      toast.error('생년월일은 과거 날짜여야 합니다.');
      return;
    }

    const clearedBank =
      businessVerificationStatus === 'BUSINESS_TYPE_NOT_ALLOWED' ||
      businessVerificationStatus === 'REJECTED';

    try {
      if (isProfileDirty) {
        await updateUser.mutateAsync({
          userId: data.userId,
          data: {
            companyName,
            ceoName,
            phoneNumber: phoneNumber.replace(/-/g, '').trim() || undefined,
            address: address || undefined,
            taxInvoiceEmail: taxInvoiceEmail || undefined,
            businessType: businessType || undefined,
            businessClass: businessClass || undefined,
            businessLicenseNumber: businessLicenseNumber.trim() || undefined,
            ...buildBusinessOwnerSaveFields({
              businessOwnerType,
              birthDate,
            }),
            accountHolder: accountHolder || undefined,
            bankName: clearedBank ? undefined : bankName || undefined,
            bankAccountNumber: clearedBank
              ? undefined
              : bankAccountNumber || undefined,
            isActive,
            remarks,
            promoLicenseNumber: promoLicenseNumber || undefined,
            promoLicenseValidFrom: promoLicenseValidFrom || undefined,
            promoLicenseValidTo: promoLicenseValidTo || undefined,
            businessVerificationStatus,
            financialVerificationStatus:
              businessVerificationStatus === 'APPROVED'
                ? financialVerificationStatus
                : 'NOT_REGISTERED',
            promoLicenseStatus:
              promoLicenseStatus === 'EXPIRED' ? undefined : promoLicenseStatus,
            // 레거시: UI에서 편집하지 않아도 저장 시 기존 trainingStatus를 함께 보내야
            // 서버가 값을 비우지 않는다 (form ?? detail ?? NOT_REGISTERED)
            trainingStatus: resolveTrainingStatusForSave(data.trainingStatus),
            salesApprovalStatus,
            businessLicenseFile: pendingBusinessLicenseKey ?? undefined,
            bankbookCopyFile: pendingBankbookKey ?? undefined,
            promoLicenseFile: pendingPromoKey ?? undefined,
          },
        });
      }

      if (consignmentDirty) {
        const consignmentOk = await consignmentRef.current?.save();
        if (!consignmentOk) return;
      }

      toast.success('사용자 정보를 저장했습니다.');
      // invalidate로 인한 stale 캐시 hydrate를 피하고, refetch 결과로만 폼을 재동기화
      const result = await refetch();
      if (result.data) {
        applyDetailToForm(result.data);
      }
    } catch (e) {
      // 서버 메시지는 필드명(businessLicenseFile 등)이 섞여 있어 운영자용 안내로 바꾼다.
      if (isInvalidUploadKeyError(e)) {
        toast.error(
          `업로드한 서류 파일 정보가 올바르지 않습니다. ${REUPLOAD_FILE_MESSAGE}`,
        );
      } else {
        handleApiError(e, toast);
      }
    }
  };

  const handleSaveClick = () => {
    if (!isFormDirty) return;
    setSaveConfirmOpen(true);
  };

  /** 목록 URL 쿼리(검색 조건)를 유지하려면 히스토리 뒤로가기가 필요하다 */
  const goBackToList = () => {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    navigate({ to: ROUTE_PATHS.USERS } as never);
  };

  const handleBackToList = () => {
    if (isFormDirty) {
      setQuitDialogOpen(true);
      return;
    }
    goBackToList();
  };

  const handleLeaveCancel = () => {
    setQuitDialogOpen(false);
    if (leaveConfirmedRef.current) return;
    blocker.reset?.();
  };

  const handleLeaveConfirm = () => {
    leaveConfirmedRef.current = true;
    setQuitDialogOpen(false);
    if (blocker.status === 'blocked') {
      blocker.proceed?.();
      return;
    }
    goBackToList();
  };

  const handlePromoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !data) return;
    try {
      assertUserDocumentFile(file.name);
      const upload = await promoUpload.mutateAsync(file.name);
      // 서버는 저장된 값을 그대로 S3 키로 써서 다운로드 URL을 만든다.
      // shortKey(UPLOAD-xxx)는 S3 객체 키가 아니므로 반드시 uploadKey를 저장한다.
      const uploadKey = requireUploadKey(upload);
      // 브라우저 file.type 이 아니라 서버가 서명한 Content-Type 으로 보내야 S3 가 403 을 내지 않는다
      await uploadFileToPresignedUrl(
        file,
        upload.presignedUrl,
        getPresignedUploadContentType(file.name),
      );
      setPendingPromoKey(uploadKey);
      toast.success('판촉 영업자 신고증을 업로드했습니다. 저장 시 반영됩니다.');
    } catch (err) {
      handleApiError(err, toast);
    } finally {
      if (promoInputRef.current) promoInputRef.current.value = '';
    }
  };

  const handleBusinessLicenseFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !data) return;
    try {
      assertUserDocumentFile(file.name);
      const upload = await businessLicenseUpload.mutateAsync(file.name);
      const uploadKey = requireUploadKey(upload);
      await uploadFileToPresignedUrl(
        file,
        upload.presignedUrl,
        getPresignedUploadContentType(file.name),
      );
      setPendingBusinessLicenseKey(uploadKey);
      toast.success('사업자등록증을 업로드했습니다. 저장 시 반영됩니다.');
    } catch (err) {
      handleApiError(err, toast);
    } finally {
      if (businessLicenseInputRef.current) {
        businessLicenseInputRef.current.value = '';
      }
    }
  };

  const handleBankbookFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !data) return;
    try {
      assertUserDocumentFile(file.name);
      const upload = await bankbookUpload.mutateAsync(file.name);
      const uploadKey = requireUploadKey(upload);
      await uploadFileToPresignedUrl(
        file,
        upload.presignedUrl,
        getPresignedUploadContentType(file.name),
      );
      setPendingBankbookKey(uploadKey);
      toast.success('통장 사본을 업로드했습니다. 저장 시 반영됩니다.');
    } catch (err) {
      handleApiError(err, toast);
    } finally {
      if (bankbookInputRef.current) bankbookInputRef.current.value = '';
    }
  };

  const handleResetPromoLicense = async () => {
    if (!data) return;
    try {
      await resetPromoLicense.mutateAsync(data.userId);
      toast.success('판촉 영업자 신고증이 초기화되었습니다.');
      const result = await refetch();
      if (result.data) {
        applyDetailToForm(result.data);
      }
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const handlePromoLicenseOcr = async () => {
    const imageUrl = data?.promoLicenseFile;
    if (!imageUrl) return;
    try {
      const ocr = await ocrPromoLicense.mutateAsync(imageUrl);
      const patch = mapPromoLicenseOcrToForm(ocr);
      if (patch.promoLicenseNumber) {
        setPromoLicenseNumber(patch.promoLicenseNumber);
      }
      if (patch.promoLicenseValidFrom) {
        setPromoLicenseValidFrom(patch.promoLicenseValidFrom);
      }
      if (patch.promoLicenseValidTo) {
        setPromoLicenseValidTo(patch.promoLicenseValidTo);
      }
      toast.success('OCR 결과를 폼에 채웠습니다. 저장해야 반영됩니다.');
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const handleBusinessLicenseOcr = async () => {
    const imageUrl = data?.businessLicenseFile;
    if (!imageUrl) return;
    try {
      const ocr = await ocrBusinessLicense.mutateAsync(imageUrl);
      const patch = mapBusinessLicenseOcrToForm(ocr);
      if (patch.companyName) setCompanyName(patch.companyName);
      if (patch.ceoName) setCeoName(patch.ceoName);
      if (patch.address) setAddress(patch.address);
      if (patch.taxInvoiceEmail) setTaxInvoiceEmail(patch.taxInvoiceEmail);
      if (patch.businessLicenseNumber) {
        setBusinessLicenseNumber(patch.businessLicenseNumber);
      }
      if (patch.businessOwnerType) {
        setBusinessOwnerType(patch.businessOwnerType);
      }
      if (patch.birthDate !== undefined) {
        setBirthDate(patch.birthDate);
      }
      toast.success('OCR 결과를 폼에 채웠습니다. 저장해야 반영됩니다.');
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  /**
   * 관리자 탈퇴 처리. 서버가 앱 탈퇴와 같은 순서로 처리해 같은 이메일·휴대폰·SNS 로 재가입할 수 있다.
   * 「이용 여부 → 이용정지」는 계정 잠금일 뿐이라 재가입이 막히므로, 재가입시키려면 이 버튼을 쓴다.
   * 되돌리는 API 가 없어 확인 팝업을 거친다.
   */
  const handleWithdraw = async () => {
    if (!data || data.withdrawnAt) return;
    const ok = await confirm({
      title: '사용자 탈퇴',
      description: [
        '탈퇴 처리하면 되돌릴 수 없습니다.',
        '· 로그인할 수 없고, 연결된 SNS 계정은 연결이 해제됩니다.',
        '· 같은 이메일·휴대폰 번호·SNS 로 새로 가입할 수 있습니다.',
        isFormDirty && '· 저장하지 않은 수정 내용은 사라집니다.',
      ]
        .filter(Boolean)
        .join('\n'),
      confirmLabel: '탈퇴',
      variant: 'destructive',
    });
    if (!ok) return;

    try {
      await withdrawUser.mutateAsync(data.userId);
      toast.success('사용자를 탈퇴 처리했습니다.');
    } catch (err) {
      // 다른 관리자가 먼저 탈퇴시킨 경우 등 — 서버는 이미 탈퇴한 사용자를 404 로 돌려준다
      if (isAxiosError(err) && err.response?.status === 404) {
        toast.error('이미 탈퇴했거나 존재하지 않는 사용자입니다.');
      } else {
        handleApiError(err, toast);
        return;
      }
    }
    const result = await refetch();
    if (result.data) {
      applyDetailToForm(result.data);
    }
  };

  const handleResetBusinessLicense = async () => {
    if (!data) return;
    try {
      await resetBusinessLicense.mutateAsync(data.userId);
      toast.success('사업자등록증이 초기화되었습니다.');
      const result = await refetch();
      if (result.data) {
        applyDetailToForm(result.data);
      }
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  const handleMaxMatchLiteReset = async () => {
    if (!data) return;
    const reason = maxMatchResetReason.trim();
    if (!reason) {
      toast.error('초기화 사유를 입력해주세요.');
      return;
    }
    try {
      await resetMaxMatchLite.mutateAsync({ userId: data.userId, reason });
      toast.success(
        '사용 가능 횟수가 초기화되었습니다. 기존 결과는 유지됩니다.',
      );
      setMaxMatchResetOpen(false);
      setMaxMatchResetReason('');
      await refetchMaxMatchLite();
    } catch (err) {
      handleApiError(err, toast);
    }
  };

  if (userId == null) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        잘못된 사용자 경로입니다.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-16">
        <LoadingSpinner tip="사용자 정보를 불러오는 중..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        사용자 정보를 찾을 수 없습니다.
      </div>
    );
  }

  const companyLabel = data.companyName || companyName || 'user';

  return (
    <div className="space-y-6">
      <ToolbarRow justify="between">
        <ToolbarRow className="gap-2">
          <h2 className="text-2xl font-semibold">사용자 상세</h2>
        </ToolbarRow>
        <ToolbarRow className="gap-2">
          <Button type="button" variant="outline" onClick={handleBackToList}>
            나가기
          </Button>
          <MenuWriteGate>
            {/* 탈퇴 계정은 서버가 404 를 주므로 미리 막는다 */}
            <Button
              type="button"
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => void handleWithdraw()}
              disabled={Boolean(data.withdrawnAt) || withdrawUser.isPending}
            >
              {data.withdrawnAt ? '탈퇴 완료' : '탈퇴'}
            </Button>
            <Button
              onClick={handleSaveClick}
              disabled={updateUser.isPending || !isFormDirty}
              className="ruda-button"
            >
              저장
            </Button>
          </MenuWriteGate>
        </ToolbarRow>
      </ToolbarRow>

      <div className="flex gap-1 border-b">
        {DETAIL_TABS.map((tab) => (
          <Button
            key={tab.key}
            type="button"
            variant={activeTab === tab.key ? 'default' : 'ghost'}
            size="sm"
            className="rounded-b-none"
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* 레거시처럼 탭 전환 시 언마운트하지 않아 수정사항 유지 */}
      <div hidden={activeTab !== 'basic'}>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">사용자 가입 정보</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label>유저 ID</Label>
                <Input disabled value={String(data.userId)} />
              </div>
              <div className="space-y-1">
                {/* 업데이트 API에 nickname 파라미터가 없어 표시 전용 */}
                <Label>유저 닉네임</Label>
                <Input disabled value={data.nickname || '-'} />
              </div>
              <div className="space-y-1">
                <Label>사용자 이메일</Label>
                <Input disabled value={data.email || '-'} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="phone">전화번호</Label>
                <Input
                  id="phone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="전화번호를 입력해주세요."
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-1">
                <Label>가입 일자</Label>
                <Input disabled value={formatDateTime(data.registeredAt)} />
              </div>
              <div className="space-y-1">
                <Label>탈퇴 일자</Label>
                <Input disabled value={formatDate(data.withdrawnAt)} />
              </div>
              <div className="space-y-1">
                <Label>이용 여부</Label>
                <Select
                  value={isActive ? 'active' : 'suspended'}
                  onValueChange={(v) => setIsActive(v === 'active')}
                  disabled={readOnly}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="suspended">이용 정지</SelectItem>
                    <SelectItem value="active">이용 가능</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>약관 동의 정보</Label>
                {termsRows.length > 0 ? (
                  <ul className="space-y-1 rounded-md border p-3 text-sm">
                    {termsRows.map((item) => (
                      <li key={item.key} className="flex items-center gap-2">
                        <span className="flex-1 truncate">{item.name}</span>
                        {item.needsReagree && (
                          <Badge variant="warning" className="shrink-0">
                            재동의 대상
                          </Badge>
                        )}
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {formatDateTimeMinute(item.agreedAt)}
                        </span>
                        <Badge
                          variant={item.agreed ? 'success' : 'destructive'}
                          className="shrink-0"
                        >
                          {item.agreed ? '동의함' : '미동의'}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    약관 정보 없음
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <UserLoginMethodsCard
            data={loginMethods.data}
            isLoading={loginMethods.isLoading}
            isError={loginMethods.isError}
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">기타 정보</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>맥스매치 라이트</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    사용 가능 횟수
                  </span>
                  <Badge
                    variant={
                      hasMaxMatchLiteUsage && maxMatchLiteRemaining === 0
                        ? 'destructive'
                        : 'info'
                    }
                  >
                    {maxMatchLiteLoading
                      ? '조회 중'
                      : hasMaxMatchLiteUsage
                        ? `${maxMatchLiteRemaining}/${maxMatchLiteLimit}`
                        : '-'}
                  </Badge>
                  <MenuWriteGate>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={
                        !hasMaxMatchLiteUsage ||
                        maxMatchLiteLoading ||
                        resetMaxMatchLite.isPending
                      }
                      onClick={() => setMaxMatchResetOpen(true)}
                    >
                      초기화
                    </Button>
                  </MenuWriteGate>
                  {hasMaxMatchLiteUsage ? (
                    <span className="text-sm text-muted-foreground">
                      이번 달 사용 {maxMatchLiteCount}회
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <Label>관리 병의원 목록</Label>
                <div className="flex flex-wrap gap-2">
                  {data.clinicNames?.length ? (
                    data.clinicNames.map((name) => (
                      <Badge key={name} variant="secondary">
                        {name}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      병의원 정보 없음
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="remarks">비고</Label>
                <Textarea
                  id="remarks"
                  rows={4}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="비고"
                  disabled={readOnly}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div hidden={activeTab !== 'business'}>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">판촉 영업자 신고증</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="promo-number">신고번호</Label>
                <Input
                  id="promo-number"
                  value={promoLicenseNumber}
                  disabled={readOnly || !hasPromoLicenseFile}
                  onChange={(e) => setPromoLicenseNumber(e.target.value)}
                  placeholder="신고번호"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label>유효 시작일</Label>
                  <DatePicker
                    value={promoLicenseValidFrom}
                    disabled={readOnly || !hasPromoLicenseFile}
                    onChange={setPromoLicenseValidFrom}
                  />
                </div>
                <div className="space-y-1">
                  <Label>유효 종료일</Label>
                  <DatePicker
                    value={promoLicenseValidTo}
                    disabled={readOnly || !hasPromoLicenseFile}
                    onChange={setPromoLicenseValidTo}
                  />
                </div>
              </div>
              <StatusSelect
                label="판촉영업 인증 상태"
                value={promoLicenseStatus}
                options={VERIFICATION_STATUS_OPTIONS}
                disabled={readOnly || !hasPromoLicenseFile}
                disabledOptions={LOCKED_VERIFICATION_OPTIONS}
                onChange={(value) => {
                  setPromoLicenseStatus(value);
                  applySalesApprovalFromVerification(
                    businessVerificationStatus,
                    financialVerificationStatus,
                    value,
                  );
                }}
              />
              {/* 레거시: URL 있으면 다운로드/보기/초기화, 없으면 업로드 */}
              {data.promoLicenseFile ? (
                <DownloadWithPreview
                  label="판촉 영업자 신고증"
                  src={data.promoLicenseFile}
                  downloadName={`${companyLabel}_판촉 영업자 신고증`}
                  onReset={canWrite ? handleResetPromoLicense : undefined}
                  resetLoading={resetPromoLicense.isPending}
                  resetConfirmTitle="판촉 영업자 신고증 초기화"
                  resetConfirmDescription="판촉 영업자 신고증 파일과 등록번호·유효기간이 삭제되고 인증 상태가 미등록으로 변경됩니다. 초기화하시겠습니까?"
                  actions={
                    canWrite ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label="판촉 영업자 신고증 OCR로 채우기"
                        disabled={ocrPromoLicense.isPending}
                        onClick={() => void handlePromoLicenseOcr()}
                      >
                        <ScanLine className="mr-2 h-4 w-4" />
                        {ocrPromoLicense.isPending
                          ? '인식 중...'
                          : 'OCR로 채우기'}
                      </Button>
                    ) : null
                  }
                />
              ) : (
                canWrite && (
                  <FileUploadField
                    label="판촉 영업자 신고증"
                    inputRef={promoInputRef}
                    uploading={promoUpload.isPending}
                    pendingKey={pendingPromoKey}
                    onChange={handlePromoFile}
                  />
                )
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">사업자 기본 정보</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <StatusSelect
                label="사업자 인증 상태"
                value={businessVerificationStatus}
                options={BUSINESS_VERIFICATION_OPTIONS}
                disabled={readOnly || !hasBusinessLicenseFile}
                disabledOptions={LOCKED_VERIFICATION_OPTIONS}
                onChange={(value) => {
                  setBusinessVerificationStatus(value);
                  applySalesApprovalFromVerification(
                    value,
                    financialVerificationStatus,
                    promoLicenseStatus,
                  );
                }}
              />
              {data.businessLicenseFile ? (
                <DownloadWithPreview
                  label="사업자등록증"
                  src={data.businessLicenseFile}
                  downloadName={`${companyLabel}_사업자등록증`}
                  onReset={canWrite ? handleResetBusinessLicense : undefined}
                  resetLoading={resetBusinessLicense.isPending}
                  resetConfirmTitle="사업자등록증 초기화"
                  resetConfirmDescription="사업자등록증 파일과 사업자등록번호·업체명·대표자명·사업자 유형·생년월일이 삭제되고 인증 상태가 미등록으로 변경됩니다. 초기화하시겠습니까?"
                  actions={
                    canWrite ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label="사업자등록증 OCR로 채우기"
                        disabled={ocrBusinessLicense.isPending}
                        onClick={() => void handleBusinessLicenseOcr()}
                      >
                        <ScanLine className="mr-2 h-4 w-4" />
                        {ocrBusinessLicense.isPending
                          ? '인식 중...'
                          : 'OCR로 채우기'}
                      </Button>
                    ) : null
                  }
                />
              ) : (
                canWrite && (
                  <FileUploadField
                    label="사업자등록증"
                    inputRef={businessLicenseInputRef}
                    uploading={businessLicenseUpload.isPending}
                    pendingKey={pendingBusinessLicenseKey}
                    onChange={handleBusinessLicenseFile}
                  />
                )
              )}
              <div className="space-y-1">
                <Label htmlFor="company-name">업체명</Label>
                <Input
                  id="company-name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="업체명"
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="business-number">사업자 등록번호</Label>
                <Input
                  id="business-number"
                  maxLength={12}
                  value={businessLicenseNumber}
                  onChange={(e) =>
                    setBusinessLicenseNumber(
                      formatBusinessRegistrationNumber(e.target.value),
                    )
                  }
                  placeholder="000-00-00000"
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ceo-name">대표자명</Label>
                <Input
                  id="ceo-name"
                  value={ceoName}
                  onChange={(e) => setCeoName(e.target.value)}
                  placeholder="대표자명"
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="business-owner-type">사업자 유형</Label>
                <Select
                  value={businessOwnerType || 'UNREGISTERED'}
                  onValueChange={(value) => {
                    if (value === 'UNREGISTERED') return;
                    setBusinessOwnerType(value as BusinessOwnerType);
                    if (value !== BUSINESS_OWNER_TYPE.INDIVIDUAL) {
                      setBirthDate('');
                    }
                  }}
                  disabled={readOnly}
                >
                  <SelectTrigger
                    id="business-owner-type"
                    aria-label="사업자 유형"
                  >
                    <SelectValue placeholder="미등록" />
                  </SelectTrigger>
                  <SelectContent>
                    {!businessOwnerType ? (
                      <SelectItem value="UNREGISTERED" disabled>
                        미등록
                      </SelectItem>
                    ) : null}
                    <SelectItem value={BUSINESS_OWNER_TYPE.INDIVIDUAL}>
                      개인사업자
                    </SelectItem>
                    <SelectItem value={BUSINESS_OWNER_TYPE.CORPORATE}>
                      법인사업자
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isIndividualBusinessOwner(businessOwnerType) ? (
                <div className="space-y-1">
                  <Label htmlFor="birth-date">생년월일</Label>
                  <DatePicker
                    id="birth-date"
                    value={birthDate}
                    onChange={setBirthDate}
                    placeholder="생년월일"
                    disabled={readOnly}
                    startMonth={BIRTH_DATE_START_MONTH}
                    endMonth={new Date()}
                    disabledDays={isTodayOrFuture}
                  />
                </div>
              ) : null}
              <div className="space-y-1">
                <Label htmlFor="tax-email">세금계산서 이메일</Label>
                <Input
                  id="tax-email"
                  type="email"
                  value={taxInvoiceEmail}
                  onChange={(e) => setTaxInvoiceEmail(e.target.value)}
                  placeholder="세금계산서 이메일"
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="business-class">업태</Label>
                <Input
                  id="business-class"
                  value={businessClass}
                  onChange={(e) => setBusinessClass(e.target.value)}
                  placeholder="업태"
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="business-type">업종</Label>
                <Input
                  id="business-type"
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                  placeholder="업종"
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-1 md:col-span-2">
                <Label htmlFor="address">주소</Label>
                <AddressSearchField
                  id="address"
                  value={address}
                  onChange={setAddress}
                  placeholder="주소"
                  disabled={readOnly}
                />
              </div>
              <StatusSelect
                label="계좌 인증 상태"
                value={financialVerificationStatus}
                options={FINANCIAL_EDIT_OPTIONS}
                disabled={readOnly}
                onChange={(value) => {
                  setFinancialVerificationStatus(value);
                  applySalesApprovalFromVerification(
                    businessVerificationStatus,
                    value,
                    promoLicenseStatus,
                  );
                }}
              />
              <div className="space-y-1">
                <Label htmlFor="bank-name">금융기관</Label>
                <Input
                  id="bank-name"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="금융기관"
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="bank-account">계좌번호</Label>
                <Input
                  id="bank-account"
                  value={bankAccountNumber}
                  onChange={(e) => setBankAccountNumber(e.target.value)}
                  placeholder="계좌번호"
                  disabled={readOnly}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="account-holder">계좌명</Label>
                <Input
                  id="account-holder"
                  value={accountHolder}
                  onChange={(e) => setAccountHolder(e.target.value)}
                  placeholder="계좌명"
                  disabled={readOnly}
                />
              </div>
              {data.bankbookCopyFile ? (
                <DownloadWithPreview
                  label="통장 사본"
                  src={data.bankbookCopyFile}
                  downloadName={`${companyLabel}_통장사본`}
                />
              ) : (
                canWrite && (
                  <FileUploadField
                    label="통장 사본"
                    inputRef={bankbookInputRef}
                    uploading={bankbookUpload.isPending}
                    pendingKey={pendingBankbookKey}
                    onChange={handleBankbookFile}
                  />
                )
              )}
              <div className="space-y-1 border-t pt-4 md:col-span-2">
                <Label>영업 인증 여부</Label>
                <Select
                  value={salesApprovalStatus}
                  onValueChange={(v) =>
                    setSalesApprovalStatus(v as SalesApprovalStatus)
                  }
                  disabled={readOnly}
                >
                  <SelectTrigger aria-label="영업 인증 여부">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SALES_APPROVAL_OPTIONS.map((option) => (
                      <SelectItem
                        key={option}
                        value={option}
                        disabled={
                          (option === 'TRANSACTION_POSSIBLE' &&
                            !salesEligibility.isTransactionEnabled) ||
                          (option === 'SETTLEMENT_POSSIBLE' &&
                            !salesEligibility.isSettlementEnabled)
                        }
                      >
                        {verificationStatusLabel(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div hidden={activeTab !== 'consignment'}>
        <UserConsignmentContractTab
          ref={consignmentRef}
          user={data}
          active={activeTab === 'consignment'}
          onDirtyChange={handleConsignmentDirtyChange}
        />
      </div>

      <AlertDialog open={saveConfirmOpen} onOpenChange={setSaveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>확인</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 내용으로 저장됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setSaveConfirmOpen(false);
                void handleSave();
              }}
            >
              확인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={quitDialogOpen}
        onOpenChange={(open) => {
          if (open) {
            leaveConfirmedRef.current = false;
            return;
          }
          handleLeaveCancel();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>나가기</AlertDialogTitle>
            <AlertDialogDescription>
              저장하지 않은 변경 사항이 있습니다. 목록으로 돌아가시겠습니까?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleLeaveCancel}>
              계속 편집
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleLeaveConfirm}>
              나가기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={maxMatchResetOpen} onOpenChange={setMaxMatchResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>맥스매치 라이트 초기화</DialogTitle>
            <DialogDescription>
              이번 달 사용 가능 횟수만 초기화되며 기존 결과와 처방 이미지는
              유지됩니다.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="초기화 사유를 입력해주세요."
            maxLength={255}
            value={maxMatchResetReason}
            onChange={(e) => setMaxMatchResetReason(e.target.value)}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setMaxMatchResetOpen(false)}
            >
              취소
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={resetMaxMatchLite.isPending}
              onClick={() => void handleMaxMatchLiteReset()}
            >
              초기화
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatusSelect = <T extends string>({
  label,
  value,
  options,
  disabled,
  disabledOptions,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  disabled?: boolean;
  disabledOptions?: readonly string[];
  onChange: (value: T) => void;
}) => {
  const optionSet = new Set<string>(options);
  const selectOptions =
    value && !optionSet.has(value)
      ? ([value, ...options] as T[])
      : [...options];

  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Select
        value={value}
        onValueChange={(v) => onChange(v as T)}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {selectOptions.map((option) => (
            <SelectItem
              key={option}
              value={option}
              disabled={disabledOptions?.includes(option)}
            >
              {verificationStatusLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
